import React, { useState, useCallback } from 'react';
import { Header } from '@/app/components/Header';
import { Footer } from '@/app/components/Footer';
import { AuthModal } from '@/app/components/AuthModal';

export function TermsOfService() {
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleSignInNeeded = useCallback(() => {
    console.log('TermsOfService: User needs to sign in');
    setShowAuthModal(true);
  }, []);

  const handleCloseAuthModal = useCallback(() => {
    setShowAuthModal(false);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      <Header onSignInNeeded={handleSignInNeeded} />
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 lg:p-12 shadow-xl border border-white/50">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
            <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

            <div className="space-y-8 text-gray-700 leading-relaxed">
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Acceptance of Terms</h2>
                <p>
                  By accessing and using Zaptopia, you accept and agree to be bound by these Terms of Service. 
                  If you do not agree to these terms, please do not use our platform.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Description of Service</h2>
                <p className="mb-4">
                  Zaptopia is a community-driven platform where users can:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Search for existing apps and discover new solutions</li>
                  <li>Submit requests for apps they need but can't find</li>
                  <li>Vote on app requests from other community members</li>
                  <li>Generate social media posts to promote app ideas</li>
                  <li>Engage with the community through comments and discussions</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">User Accounts</h2>
                <p className="mb-4">
                  To access certain features, you must create an account. You are responsible for:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Providing accurate and complete information</li>
                  <li>Maintaining the security of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Notifying us immediately of any unauthorized access</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Content Guidelines</h2>
                
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Acceptable Use</h3>
                <p className="mb-4">When submitting content to Zaptopia, you agree to:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Submit genuine app requests that would benefit others</li>
                  <li>Provide clear, helpful descriptions of app needs</li>
                  <li>Vote honestly based on your actual interest in app ideas</li>
                  <li>Be respectful and constructive in community interactions</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-6">Prohibited Content</h3>
                <p className="mb-4">You may not submit content that:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Violates any laws or regulations</li>
                  <li>Infringes on intellectual property rights</li>
                  <li>Contains harmful, threatening, or abusive language</li>
                  <li>Includes spam, duplicate requests, or low-quality submissions</li>
                  <li>Promotes illegal activities or harmful services</li>
                  <li>Contains personal information of others without consent</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Intellectual Property</h2>
                
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Your Content</h3>
                <p className="mb-4">
                  You retain ownership of content you submit to Zaptopia. However, by submitting content, 
                  you grant us a worldwide, royalty-free license to use, display, and distribute your 
                  content in connection with the platform.
                </p>

                <h3 className="text-lg font-semibold text-gray-800 mb-2">Our Platform</h3>
                <p>
                  Zaptopia and its original content, features, and functionality are owned by ZAPT and 
                  protected by international copyright, trademark, and other intellectual property laws.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Community Standards</h2>
                <p className="mb-4">
                  Zaptopia is built on community trust and collaboration. We expect all users to:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Treat other community members with respect</li>
                  <li>Contribute meaningfully to discussions</li>
                  <li>Help maintain the quality of app requests and votes</li>
                  <li>Report inappropriate content or behavior</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Voting and Engagement</h2>
                <p className="mb-4">
                  Our voting system helps surface the most wanted app ideas. You agree to:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Vote based on genuine interest and need</li>
                  <li>Not manipulate voting through fake accounts or coordination</li>
                  <li>Understand that votes help prioritize development but don't guarantee app creation</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Share Post Generator</h2>
                <p className="mb-4">
                  Our AI-powered share post generator helps you promote app ideas on social media. 
                  When using this feature:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Review generated content before sharing</li>
                  <li>Ensure shared content complies with platform-specific rules</li>
                  <li>Don't use the feature for spam or manipulative purposes</li>
                  <li>Respect the communities where you share content</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Disclaimers</h2>
                <p className="mb-4">
                  <strong>No Guarantee of App Development:</strong> Zaptopia is a discovery and community 
                  platform. We don't guarantee that highly voted app requests will be developed.
                </p>
                <p className="mb-4">
                  <strong>Third-Party Content:</strong> User-submitted content reflects individual opinions 
                  and needs, not our endorsement.
                </p>
                <p>
                  <strong>Service Availability:</strong> We strive for reliable service but can't guarantee 
                  uninterrupted access or perfect functionality.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Termination</h2>
                <p className="mb-4">
                  We reserve the right to suspend or terminate accounts that violate these terms or 
                  engage in harmful behavior. You may also delete your account at any time.
                </p>
                <p>
                  Upon termination, your access to the platform will cease, but publicly shared content 
                  may remain visible to maintain community continuity.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
                <p>
                  To the fullest extent permitted by law, ZAPT shall not be liable for any indirect, 
                  incidental, special, consequential, or punitive damages resulting from your use of 
                  Zaptopia or inability to use the service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to Terms</h2>
                <p>
                  We may modify these Terms of Service at any time. We'll notify users of significant 
                  changes via email or platform announcements. Continued use after changes constitutes 
                  acceptance of the updated terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Governing Law</h2>
                <p>
                  These terms are governed by the laws of the jurisdiction where ZAPT operates. 
                  Any disputes will be resolved through binding arbitration or in the appropriate courts.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
                <p>
                  If you have questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                  <p><strong>Email:</strong> info@zapt.ai</p>
                  <p><strong>Subject Line:</strong> Terms of Service Question</p>
                </div>
              </section>

              <section className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-blue-900 mb-3">Building the Future of App Discovery</h2>
                <p className="text-blue-800">
                  Thank you for being part of the Zaptopia community! Together, we're creating a better way 
                  for people to discover the apps they need and for developers to understand what to build next.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={handleCloseAuthModal} 
      />
    </div>
  );
} 